import { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import { useGraphQLMutation } from '@tviklax/ui-components';
import { AddNotePayload, ActivityLogEntry, HealthStatus } from './SchedulingContextTypes';

interface UseAddNoteParams {
  orderId: string;
  backendOrderId?: number;
  orderShipmentId?: number;
  onActivityLogUpdate?: (newEntry: ActivityLogEntry) => void;
  onHealthUpdate?: (health: HealthStatus) => void;
}

const CREATE_ORDER_SHIPMENT_LOG_MUTATION = gql`
  mutation CreateOrderShipmentLog($data: CreateOrderShipmentLogsInput!) {
    createOrderShipmentLogs(data: $data) {
      id
      orderId
      orderShipmentId
      note
      statusChangedAt
    }
  }
`;

const SEND_PAYMENT_REMINDER_MUTATION = gql`
  mutation SendPaymentReminder($input: SendPaymentReminderInput!) {
    sendPaymentReminder(input: $input) {
      success
      message
      orderId
      actionType
      timestamp
    }
  }
`;

export const useAddNote = ({ orderId, backendOrderId, orderShipmentId, onActivityLogUpdate, onHealthUpdate }: UseAddNoteParams) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOrderShipmentLog] = useGraphQLMutation(CREATE_ORDER_SHIPMENT_LOG_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });
  const [sendPaymentReminder] = useGraphQLMutation(SEND_PAYMENT_REMINDER_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });

  const handleSaveNote = useCallback(
    async (payload: AddNotePayload) => {
      setIsSubmitting(true);
      setError(null);

      try {
        if (backendOrderId && orderShipmentId) {
          await createOrderShipmentLog({
            variables: {
              data: {
                orderId: backendOrderId,
                orderShipmentId,
                note: payload.content,
                oldStatus: null,
                newStatus: null,
                statusChangeUserType: 2,
                statusChangedBy: null,
                statusChangedAt: payload.timestamp,
                shippingResponse: JSON.stringify({
                  noteType: payload.noteType,
                  impactLevel: payload.impactLevel,
                  visibility: payload.visibility,
                  currentStage: payload.currentStage,
                  nextAction: payload.nextAction || null,
                  nextActionDueBy: payload.nextActionDueBy || null,
                }),
              },
            },
          });
        }

        const newActivity: ActivityLogEntry = {
          id: `note-${Date.now()}`,
          timestamp: payload.timestamp,
          type: 'human',
          actor: payload.author,
          action: `Added ${payload.noteType} note`,
          details: payload.content,
        };

        // Notify parent of activity log update
        onActivityLogUpdate?.(newActivity);

        // If impact is delay-expected, optionally update health status
        if (payload.impactLevel === 'delay-expected') {
          onHealthUpdate?.('at-risk');
        }

        // Send notifications if enabled
        if (payload.notifications?.send) {
          if (payload.notifications.internal) {
            await sendPaymentReminder({
              variables: {
                input: {
                  orderId,
                  recipient: 'internal',
                  message: `[Scheduling] ${newActivity.action}: ${newActivity.details}`,
                  actor: payload.author,
                },
              },
            });
          }
          if (payload.notifications.customer) {
            await sendPaymentReminder({
              variables: {
                input: {
                  orderId,
                  recipient: 'customer',
                  message: `[Scheduling] ${newActivity.action}: ${newActivity.details}`,
                  actor: payload.author,
                },
              },
            });
          }
          if (payload.notifications.ops) {
            await sendPaymentReminder({
              variables: {
                input: {
                  orderId,
                  recipient: 'ops',
                  message: `[Scheduling] ${newActivity.action}: ${newActivity.details}`,
                  actor: payload.author,
                },
              },
            });
          }
        }

        return { success: true, activityEntry: newActivity };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save note';
        setError(message);
        return { success: false, error: message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [backendOrderId, createOrderShipmentLog, onActivityLogUpdate, onHealthUpdate, orderId, orderShipmentId, sendPaymentReminder]
  );

  return { handleSaveNote, isSubmitting, error };
};
